// The active map and the map editor's draft. Not persisted.
import { createSlice } from '@reduxjs/toolkit'

const HISTORY = 50

const initialState = {
	// This window's pick; null falls back to the project's `settings.map_id`.
	active_id: null,
	// What a window with no project (`/output`) shows, and the editor's guides and card over it: the runner sends them.
	remote: null,
	remote_guides: null,
	remote_card: 'off',
	// While the editor is open. `point`: the active anchor `{ sp, n }` or quad corner index.
	editing: null,
}

const CLEARS_POINT = ['tab', 'selected', 'tool']

const mapsSlice = createSlice({
	name: 'maps',
	initialState,
	reducers: {
		setActiveMapId: (s, { payload }) => {
			s.active_id = payload
		},
		setRemoteMap: (s, { payload: { map, guides, card = 'off' } }) => {
			s.remote = map
			s.remote_guides = guides
			s.remote_card = card
		},
		openMapEditor: (s, { payload: { id, name, data, tab = 'output' } }) => {
			s.editing = {
				id,
				name,
				draft: data,
				saved: JSON.stringify(data),
				tab,
				selected: null,
				point: null,
				selection_ids: [],
				tool: 'select',
				past: [],
				future: [],
			}
		},
		closeMapEditor: s => {
			s.editing = null
		},
		/** One undoable step of the draft. */
		editMap: (s, { payload }) => {
			const e = s.editing
			if (!e) return
			e.past.push(e.draft)
			if (e.past.length > HISTORY) e.past.shift()
			e.future = []
			e.draft = payload
		},
		undoMap: s => {
			const e = s.editing
			if (!e?.past.length) return
			e.future.push(e.draft)
			e.draft = e.past.pop()
		},
		redoMap: s => {
			const e = s.editing
			if (!e?.future.length) return
			e.past.push(e.draft)
			e.draft = e.future.pop()
		},
		/** `payload`: the saved JSON to restore after a failed save; none marks the draft saved. */
		markMapSaved: (s, { payload }) => {
			if (s.editing) s.editing.saved = payload ?? JSON.stringify(s.editing.draft)
		},
		setMapEditor: (s, { payload }) => {
			if (!s.editing) return
			if (!('point' in payload) && CLEARS_POINT.some(k => k in payload)) s.editing.point = null
			if (!('selection_ids' in payload) && 'tab' in payload) s.editing.selection_ids = []
			Object.assign(s.editing, payload)
			// The corner-pin tool edits the projector, not a selection: picking it clears the selection, a selection drops it.
			if (payload.tool === 'pin') Object.assign(s.editing, { selected: null, selection_ids: [] })
			else if (payload.selected && s.editing.tool === 'pin') s.editing.tool = 'select'
		},
	},
})

export const {
	setActiveMapId,
	setRemoteMap,
	openMapEditor,
	closeMapEditor,
	editMap,
	undoMap,
	redoMap,
	markMapSaved,
	setMapEditor,
} = mapsSlice.actions

export default mapsSlice.reducer
